import React from "react";
import { Link } from "react-router-dom";
import "./KitchenAccessoriesPage.css";

const accessories = [
  {
    title: "Cutlery and drawer organisers",
    summary:
      "Drawer organisers divide a drawer into useful sections for cutlery, peelers, serving spoons and the small tools you reach for every day. Giving each item a place makes it easier to find what you need and keep the drawer tidy.",
    ideal: "Cutlery, serving spoons, peelers and everyday kitchen tools.",
    benefit: "Keeps small utensils arranged and quick to reach.",
  },
  {
    title: "Tall pull-out pantry",
    summary:
      "A tall pantry pull-out uses vertical cabinet space for groceries, dry foods, snacks and containers. Pulling the shelves forward makes the contents easier to see than in a deep fixed cabinet, including items stored towards the back.",
    ideal: "Groceries, dry foods, snacks, containers and pantry supplies.",
    benefit: "Makes tall storage visible and accessible in one movement.",
  },
  {
    title: "Spice and masala pull-out",
    summary:
      "A dedicated spice pull-out keeps masala boxes, jars and seasonings together near the cooking zone. Arrange the spices you use most often at the front, and use the remaining shelves for occasional ingredients.",
    ideal: "Masala boxes, spice jars, seasonings and cooking condiments.",
    benefit: "Keeps frequently used spices organised close to the hob.",
  },
  {
    title: "Bottle pull-out",
    summary:
      "Oil, vinegar, sauces and other tall containers can be awkward to store in standard drawers. A slim bottle pull-out gives them a dedicated vertical space and helps keep bottles upright and easy to reach.",
    ideal: "Cooking oils, sauces, vinegar, ghee and tall bottles.",
    benefit: "Turns a narrow cabinet space into convenient bottle storage.",
  },
  {
    title: "Storage baskets",
    summary:
      "Open storage baskets make it easier to group similar kitchen essentials and see what is inside. Depending on the cabinet layout, baskets can be fitted into base units, tall units or other storage areas.",
    ideal: "Packaged foods, groceries, produce and everyday supplies.",
    benefit: "Groups similar items and makes cabinet contents easier to scan.",
  },
  {
    title: "Corner carousel",
    summary:
      "A corner carousel brings items stored deep inside an L-shaped or U-shaped kitchen cabinet closer to you. Rotating shelves can make an awkward corner more useful for larger items that need accessible storage.",
    ideal: "Pots, pans, cookware and larger kitchen essentials.",
    benefit: "Makes difficult corner storage easier to reach.",
  },
  {
    title: "Roller shutter cabinet",
    summary:
      "A roller shutter cabinet stores frequently used appliances and essentials behind a vertically moving shutter. Since the shutter does not swing outward, it can be useful where counter or walkway space is limited.",
    ideal: "Coffee machines, mixers, small appliances and jars.",
    benefit: "Keeps everyday items accessible while clearing the worktop.",
  },
  {
    title: "Under-sink organisers",
    summary:
      "Plumbing can make the cabinet beneath a sink difficult to use. Pull-out trays and organisers designed around the pipes can create practical storage for cleaning supplies without treating the whole cabinet as one open compartment.",
    ideal: "Dishwashing liquid, scrubbers, brushes, cloths and garbage bags.",
    benefit: "Makes an awkward cabinet easier to organise and use.",
  },
  {
    title: "Deep tandem drawers",
    summary:
      "Deep drawers bring bulky cookware towards you instead of leaving it stacked at the back of a base cabinet. They are useful for heavier items common in Indian cooking, such as pressure cookers, kadais and large containers.",
    ideal: "Pots, pans, kadais, pressure cookers and large vessels.",
    benefit: "Improves access to bulky cookware stored in deep cabinets.",
  },
  {
    title: "Cleaning pull-out",
    summary:
      "A dedicated cleaning pull-out keeps sink and cleaning essentials together. A narrow unit can make use of cabinet space that may be too small for a conventional drawer or cupboard.",
    ideal: "Dishwashing liquid, detergents, brushes and scrubbers.",
    benefit: "Keeps cleaning products together and the sink area clearer.",
  },
];

const faqs = [
  {
    question: "What are the most useful modular kitchen accessories?",
    answer:
      "Useful options include drawer organisers, spice and bottle pull-outs, pantry units, corner storage, under-sink organisers and deep drawers. The right combination depends on your layout and storage needs.",
  },
  {
    question: "Which kitchen accessories work well in a small kitchen?",
    answer:
      "Slim pull-outs, drawer organisers and vertical pantry storage can make better use of limited cabinet space. Choose accessories to suit the actual dimensions of your kitchen.",
  },
  {
    question: "Which accessories are useful for Indian cooking?",
    answer:
      "Spice storage, bottle pull-outs, deep drawers for cookware, corner storage and under-sink organisers are practical choices for many Indian kitchens. The best selection depends on cooking habits and cookware.",
  },
  {
    question: "Do I need to install every modular kitchen accessory?",
    answer:
      "No. Select accessories based on how you use your kitchen. The goal is to solve genuine organisation or accessibility problems, not to fill every cabinet with mechanisms.",
  },
  {
    question: "How should I choose accessories for my kitchen?",
    answer:
      "Consider your layout, cabinet dimensions, cooking habits, cookware sizes and the items you use most often. A kitchen designer can help match storage solutions to the available space.",
  },
];

const KitchenAccessoriesPage: React.FC = () => (
  <main className="kitchen-article">
    <div className="kitchen-article__topline">
      <Link to="/" className="kitchen-article__breadcrumb">Home</Link>
      <span aria-hidden="true">/</span>
      <span>Kitchen storage guide</span>
    </div>

    <header className="kitchen-article__hero">
      <p className="kitchen-article__eyebrow">A practical guide for Indian homes</p>
      <h1>10 Smart Space-Saving Modular Kitchen Accessories for Indian Homes</h1>
      <p className="kitchen-article__lead">
        A beautiful kitchen should also be easy to use. With the right modular
        kitchen accessories, even a compact kitchen can offer efficient storage.
        Thoughtful organisers, pull-outs, drawer systems and corner solutions
        help keep everyday essentials within easy reach.
      </p>
      <p>
        At Vista Fusion Studio, we plan storage around the way you cook and use
        your kitchen. From spices and cutlery to cookware, cleaning supplies and
        pantry essentials, the right accessories can make unused or difficult
        spaces more practical.
      </p>
      <div className="kitchen-article__project-gallery">
        <figure className="kitchen-article__project-image">
          <img
            src="/images/kitchen.jpeg"
            alt="L-shaped modular kitchen with pantry pull-outs, deep drawers and corner storage"
            loading="lazy"
          />
          <figcaption>Pull-outs, deep drawers and corner storage make essentials easier to access.</figcaption>
        </figure>
      </div>
    </header>

    <nav className="kitchen-article__contents" aria-label="Contents">
      <h2>Explore the 10 accessories</h2>
      <ol>
        {accessories.map((item, index) => (
          <li key={item.title}>
            <a
              href={`#accessory-${index + 1}`}
              onClick={(event) => {
                event.preventDefault();
                window.history.replaceState(
                  window.history.state,
                  "",
                  `${window.location.pathname}${window.location.search}`,
                );
                document.getElementById(`accessory-${index + 1}`)?.scrollIntoView({
                  behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
                    ? "auto"
                    : "smooth",
                  block: "start",
                });
              }}
            >
              {item.title}
            </a>
          </li>
        ))}
      </ol>
    </nav>

    <section className="kitchen-article__list" aria-label="Kitchen accessories">
      {accessories.map((item, index) => (
        <article
          className="kitchen-article__item"
          id={`accessory-${index + 1}`}
          key={item.title}
        >
          <p className="kitchen-article__number">{index + 1}</p>
          <div>
            <h2>{item.title}</h2>
            <p>{item.summary}</p>
            <dl>
              <div><dt>Ideal for</dt><dd>{item.ideal}</dd></div>
              <div><dt>Why choose it</dt><dd>{item.benefit}</dd></div>
            </dl>
          </div>
        </article>
      ))}
    </section>

    <section className="kitchen-article__planning" id="kitchen-planning">
      <p className="kitchen-article__eyebrow">Plan around your routine</p>
      <h2>Choose kitchen storage that suits the way you cook</h2>
      <p>
        Not every kitchen needs every accessory. The right storage plan depends
        on your layout, cabinet dimensions, cooking habits and the items you use
        most often. Before choosing, consider:
      </p>
      <ul>
        <li><strong>Kitchen layout:</strong> L-shaped, U-shaped, parallel and straight kitchens have different storage needs.</li>
        <li><strong>Cooking habits:</strong> Keep frequently used ingredients close to the cooking zone.</li>
        <li><strong>Cabinet dimensions:</strong> Match accessories to the available width, depth and height.</li>
        <li><strong>Cookware:</strong> Allow for larger vessels, pressure cookers, kadais and tawas.</li>
        <li><strong>Accessibility:</strong> Make everyday items easier to reach without unnecessary bending or searching.</li>
      </ul>
      <p>
        The goal is not to fill every cabinet with mechanisms. It is to give
        important items a logical, accessible place. Learn about our{" "}
        <Link to="/services">interior design services</Link> or explore our{" "}
        <Link to="/#service-section">home interior design services</Link>.
      </p>
    </section>

    <section className="kitchen-article__faqs" aria-labelledby="kitchen-faq-heading">
      <p className="kitchen-article__eyebrow">Common questions</p>
      <h2 id="kitchen-faq-heading">Modular kitchen storage FAQs</h2>
      {faqs.map((faq) => (
        <details key={faq.question}>
          <summary>{faq.question}</summary>
          <p>{faq.answer}</p>
        </details>
      ))}
    </section>

    <section className="kitchen-article__cta">
      <p className="kitchen-article__eyebrow">Designed around your home</p>
      <h2>Your kitchen should fit your lifestyle</h2>
      <p>
        Whether you are planning a compact apartment kitchen, upgrading an
        existing space or designing a complete modular kitchen, the right
        storage can help you use the available space more intelligently. Vista
        Fusion Studio serves homeowners in Hyderabad with design solutions
        planned around their space and routine.
      </p>
      <div className="kitchen-article__actions">
        <Link className="kitchen-article__button" to="/contact-us">Plan your modular kitchen</Link>
        <Link className="kitchen-article__secondary" to="/services">Explore our services</Link>
      </div>
    </section>
  </main>
);

export default KitchenAccessoriesPage;
